import { IObject } from '../../../../type_definitions/datatypes';
import { Injectable, Type } from '@angular/core';
import { IReportAggregates, IReportData } from '../../../shared/report/report-data.service';
import { FormattingService } from '../../../shared/services/formatting.service';
import { Dictionary } from '../../../../type_definitions/vendor';
import { Chart } from '../chart/base-chart.component';

import { TEST_DASHBOARD_CONFIG } from './config/TEST-dashboard.config';
import { OD_ORDER_DASHBOARD_CONFIG } from './config/OD_ORDER-dashboard.config';
import { OD_OD_DASHBOARD_CONFIG } from './config/OD_OD-dashboard.config';
import { OD_BP_DASHBOARD_CONFIG } from './config/OD_BP-dashboard.config';
import { CUSTOMER_DASHBOARD_CONFIG } from './config/CUSTOMER-dashboard.config';
import { GBDM_DASHBOARD_CONFIG } from './config/GBDM-dashboard.config';
import { GBDM_NEW_DASHBOARD_CONFIG } from './config/GBDM_NEW-dashboard.config';
import { SALES_DASHBOARD_CONFIG } from './config/SALES_DASHBOARD-dashboard.config';
import { SALES_KPI_DASHBOARD_CONFIG } from './config/SALES_KPI-dashboard.config';
import { URGENT_DASHBOARD_CONFIG } from './config/URGENT-dashboard.config';
import { LBDM_DASHBOARD_CONFIG } from './config/LBDM-dashboard.config';
import { ACTIVATION_DASHBOARD_CONFIG } from './config/ACTIVATION-dashboard.config';
import { OEM_KPI_DASHBOARD_CONFIG } from './config/OEM_KPI-dashboard.config';
import { INTEL_KPI_DASHBOARD_CONFIG } from './config/INTEL_KPI-dashboard.config';
import { BP_SETUP_KPI_DASHBOARD_CONFIG } from './config/BP_SETUP_KPI-dashboard.config';
import { COUNTRY_HIT_RATE_DASHBOARD_CONFIG } from './config/COUNTRY_HIT_RATE-dashboard.config';
import { WEBSHOP_HIT_RATE_DASHBOARD_CONFIG } from './config/WEBSHOP_HIT_RATE-dashboard.config';
import { GridSortType } from '../../../shared/report/report-grid.service';

export interface IDashboardSectionMetadata extends IDashboardReport {
  key:string;
  code?:string;
  comments?:string;
  alternateReport?:IDashboardReport;
  columns?:(string | IDashboardSectionColumn)[];
  sort?:IDashboardSort[];
  restricted?:boolean;
  validations?: IDashboardSectionValidation[]; // this section won't be loaded unless validation is passed
  extraStatus?:IDashboardExtraSectionStatus;
  aggregates?:IReportAggregates;
  indicatorFn?:(reportData:IReportData, ctx?:IRowCountCtx) => number|string;
  infoComponent?:IDashboardComponent;
  extraTextValue?:IDashboardReportExtraTextValue;
  gridHeight?:string;
  partialRender?: boolean;
  storedReport?:string;
  exportTemplate?:string;
}

export interface IDashboardGroupMetadata {
  key:string;
  reports:IDashboardSectionMetadata[];
}

export interface IDashboardSectionValidation {
  invalid?: boolean;
  expression: (model: IObject) => boolean;
  message: string;
}

export interface IDashboardExportMetadata {
  id:number;
  key:string;
  templateCode?:string; // optional when the report has only 1 template
  filters?:IObject; // overwrite the metadata filters used in the export
  mapping?:IObject;
}

export interface IDashboardReport {
  id?:number;
  browselistCode?:string;
  storedReport?:string;
  exportTemplate?:string;
  fixedFilters?:{};
  mapping?:{};
}

export interface IDashboardReportExtra extends IDashboardReport {
  name:string;
}

export interface IDashboardReportExtraTextValue {
  report:IDashboardReportExtra;
}

export interface IDashboardSectionColumn {
  name:string;
  label?:string;
  groupingSequence?:number;
  aggregate?:string;
}

export interface IDashboardSort {
  name:string;
  direction?:GridSortType;
}

export interface IDashboardExtraSectionStatus {
  defaultValue:IDashboardExtraSectionStatusValue;
  newValue?:IDashboardExtraSectionStatusValue;
  checkType?:IDashboardExtraSectionStatusValueCheckType;
}

export const enum IDashboardExtraSectionStatusValue {
  NA,
  AVAILABLE,
  OPTIONAL_NOT_AVAILABLE,
  MANDATORY_NOT_AVAILABLE
}

export const enum IDashboardExtraSectionStatusValueCheckType {
  GT,
  EQ
}

export interface IDashboardComponent {
  name?:Type<any>;
  inputs?:IObject;
  outputs?:IObject;
}

export interface IDashboardMetadata {
  title:string;
  comments?:string;
  filterComponent:IDashboardComponent;
  subtitleComponent?:IDashboardComponent;
  headerComponent?:IDashboardComponent;
  footerComponent?:IDashboardComponent;
  chartLink?:Chart;
  mapping:IObject;
  extraMapping?:IObject; // extraMapping is not used on the report level but is used on dashboard level for dashboard filters to params / params to dashboard filters; when one of the fields is missing in 'mapping' (because it's set at the report level) -> should have to be defined here
  fixedFilters?:IObject;
  export?: {
    reports:IDashboardExportMetadata[];
  };
  reports?:IDashboardSectionMetadata[];
  groups?:IDashboardGroupMetadata[];
}

export interface IRowCountCtx {
  formattingService:FormattingService;
}

export enum DashboardCode {
  TEST = 'TEST',
  OD_ORDER = 'OD_ORDER',
  OD_BP = 'OD_BP',
  OD_OD = 'OD_OD',
  CUSTOMER = 'CUSTOMER',
  GBDM = 'GBDM',
  GBDM_NEW = 'GBDM_NEW',
  SALES_KPI = 'SALES_KPI',
  SALES_DASHBOARD = 'SALES_DASHBOARD',
  URGENT = 'URGENT',
  LBDM = 'LBDM',
  ACTIVATION_KPI = 'ACTIVATION_KPI',
  OEM_KPI = 'OEM_KPI',
  INTEL_KPI = 'INTEL_KPI',
  BP_SETUP_KPI = 'BP_SETUP_KPI',
  COUNTRY_HIT_RATE = 'COUNTRY_HIT_RATE',
  WEBSHOP_HIT_RATE = 'WEBSHOP_HIT_RATE'
}

@Injectable()
export class DashboardMetadataService {

  private metadata:{ [ code:string ]:IDashboardMetadata; } = {
    [DashboardCode.TEST]: TEST_DASHBOARD_CONFIG,
    [DashboardCode.OD_ORDER]: OD_ORDER_DASHBOARD_CONFIG,
    [DashboardCode.OD_BP]: OD_BP_DASHBOARD_CONFIG,
    [DashboardCode.OD_OD]: OD_OD_DASHBOARD_CONFIG,
    [DashboardCode.CUSTOMER]: CUSTOMER_DASHBOARD_CONFIG,
    [DashboardCode.GBDM]: GBDM_DASHBOARD_CONFIG,
    [DashboardCode.GBDM_NEW]: GBDM_NEW_DASHBOARD_CONFIG,
    [DashboardCode.SALES_KPI]: SALES_KPI_DASHBOARD_CONFIG,
    [DashboardCode.SALES_DASHBOARD]: SALES_DASHBOARD_CONFIG,
    [DashboardCode.URGENT]: URGENT_DASHBOARD_CONFIG,
    [DashboardCode.LBDM]: LBDM_DASHBOARD_CONFIG,
    [DashboardCode.ACTIVATION_KPI]: ACTIVATION_DASHBOARD_CONFIG,
    [DashboardCode.OEM_KPI]: OEM_KPI_DASHBOARD_CONFIG,
    [DashboardCode.INTEL_KPI]: INTEL_KPI_DASHBOARD_CONFIG,
    [DashboardCode.BP_SETUP_KPI]: BP_SETUP_KPI_DASHBOARD_CONFIG,
    [DashboardCode.COUNTRY_HIT_RATE]: COUNTRY_HIT_RATE_DASHBOARD_CONFIG,
    [DashboardCode.WEBSHOP_HIT_RATE]: WEBSHOP_HIT_RATE_DASHBOARD_CONFIG
  };

  constructor() {
    /***/
  }

  public getMetaData(code:DashboardCode):IDashboardMetadata {
    return this.metadata[ code ];
  }

  public getMetadataList():Dictionary<IDashboardMetadata> {
    return this.metadata;
  }
}